import { useEffect, useRef } from 'react'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectLesson } from '../../editorSlice'
import { useLessonLastBlock } from './useLessonLastBlock'

const keyOf = (courseId: number) => `oa-editor-lesson:${courseId}`

const read = (courseId: number): number | null => {
  try {
    const v = Number(localStorage.getItem(keyOf(courseId)))
    return Number.isFinite(v) && v > 0 ? v : null
  } catch {
    return null
  }
}

/**
 * Выбранный урок переживает обновление страницы (запоминается в браузере, по курсу),
 * а при первом входе в курс с уроками сразу выбран первый. Экран внутри урока
 * выбирает `useLessonLastBlock`.
 */
export const useRememberedLesson = (course: CourseWithoutBlocks) => {
  const dispatch = useAppDispatch()
  const selected = useAppSelector((s) => s.editor.selectedLessonId)
  const seededFor = useRef<number | null>(null)
  // Вместе с уроком сразу выделен его экран (последний за сессию или первый).
  useLessonLastBlock()

  // Засев — один раз на курс, когда уроки уже известны.
  useEffect(() => {
    if (seededFor.current === course.id || course.lessons.length === 0) return
    seededFor.current = course.id
    if (selected !== null) return
    const stored = read(course.id)
    const target = course.lessons.find((l) => l.id === stored) ?? course.lessons[0]
    dispatch(selectLesson(target.id))
  }, [course.id, course.lessons, selected, dispatch])

  useEffect(() => {
    if (selected === null) return
    try {
      localStorage.setItem(keyOf(course.id), String(selected))
    } catch {
      // приватный режим — выбор просто не запомнится
    }
  }, [course.id, selected])
}
